import type { SystemFieldRow } from "./types";

// Content types are read-only in the admin (defined in code). These helpers support the read-only
// viewer only: title/key normalisation for the reserved-type badge, and flattening a system content
// type's fields into indented preview rows. The former create/edit helpers were removed with the
// editor.

export const normalizeTitle = (title?: string | null) => (title ?? "").trim().toLowerCase();
export const normalizeKey = (title?: string | null) =>
	normalizeTitle(title)
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

export const SYSTEM_CONTENT_TYPE_KEYS = ["layout", "languages", "contact-info", "page"];
export const RESERVED_CONTENT_TYPE_KEYS = ["layout", "language", "languages", "contact-info", "page"];

export const isReservedTitle = (title?: string | null) => RESERVED_CONTENT_TYPE_KEYS.includes(normalizeKey(title));

export const buildSystemFieldRows = (fields: any[] = [], parentPath = "", depth = 0): SystemFieldRow[] => {
	const rows: SystemFieldRow[] = [];
	fields.forEach((field) => {
		const name = field?.name ?? field?.displayName ?? "field";
		const label = field?.displayName ?? field?.name ?? "Field";
		const kind = field?.kind ?? field?.uiHint ?? "—";
		const required = Boolean(field?.required);
		const description = field?.description ?? "—";
		const options = Array.isArray(field?.options) ? field.options : [];
		const path = parentPath ? `${parentPath}.${name}` : name;
		rows.push({ path, label, kind, required, description, options, depth });

		if (field?.kind === "array" && field?.items) {
			const itemLabel = `${label} item`;
			const itemPath = `${path}[]`;
			const itemKind = field.items?.kind ?? field.items?.uiHint ?? "—";
			const itemRequired = Boolean(field.items?.required);
			const itemDescription = field.items?.description ?? "—";
			const itemOptions = Array.isArray(field.items?.options) ? field.items.options : [];
			rows.push({
				path: itemPath,
				label: itemLabel,
				kind: itemKind,
				required: itemRequired,
				description: itemDescription,
				options: itemOptions,
				depth: depth + 1
			});
			if (Array.isArray(field.items?.properties) && field.items.properties.length > 0) {
				rows.push(...buildSystemFieldRows(field.items.properties, itemPath, depth + 2));
			}
		}

		if (Array.isArray(field?.properties) && field.properties.length > 0) {
			rows.push(...buildSystemFieldRows(field.properties, path, depth + 1));
		}
	});
	return rows;
};
